import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays } from 'date-fns';
import type { RecurrenceType } from '../../RecurrenceSelector';
import type { EndType } from '../../EndDateSelector';
import type {
  RecurringData,
  DateRangeData,
  OneTimeData,
  ServiceOption,
  ProviderOption,
} from '../../CreateAvailabilityDialog';
import type { AvailabilityServiceType } from '../steps/ServiceTypeStep';
import type { GroupRecurrenceMode } from '../steps/GroupRecurrenceStep';

export type AvailabilityMode = 'recurring' | 'date_range' | 'one_time';
export type WizardStep = 0 | 1 | 2 | 3 | 4 | 5; // 0 = service-type picker (or welcome in first-time flow)
export type StartType = 'immediately' | 'on_date';

// Group-class create payloads — separate from the legacy RecurringData/OneTimeData
// so the page-level wiring can dispatch to the new endpoints.
export interface GroupRecurringData {
  service_id: number;
  days_of_week: number[];
  start_time: string;
  recurrence_type: RecurrenceType;
  recurrence_interval: number;
  monthly_week: number | null;
  monthly_day: number | null;
  end_type: EndType;
  end_date: string | null;
  occurrences: number | null;
  effective_from?: string;
  provider_id?: number;
}
export interface GroupOneTimeData {
  service_id: number;
  date: string;
  start_time: string;
  provider_id?: number;
}

// Service creation data for first-time flow
export interface NewServiceData {
  name: string;
  duration: number;
  price: string;
}

const DAY_FULL_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const WEEK_OPTION_KEYS: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  '-1': 'last',
};

export interface UseAvailabilityWizardProps {
  onCreateRecurring: (data: RecurringData) => Promise<void>;
  onCreateDateRange: (data: DateRangeData) => Promise<void>;
  onCreateOneTime: (data: OneTimeData) => Promise<void>;
  // Group-class branch — both required when group flow is enabled.
  onCreateGroupRecurring?: (data: GroupRecurringData) => Promise<void>;
  onCreateGroupOneTime?: (data: GroupOneTimeData) => Promise<void>;
  onCreateService?: (data: NewServiceData) => Promise<ServiceOption>;
  services?: ServiceOption[];
  providers?: ProviderOption[];
  selectedProviderId?: number | null;
  onClose: () => void;
  currency?: string;
  initialStep?: WizardStep;
  // Prefill one-time date/start time (used when wizard opens from a grid
  // cell click). Sets mode to 'one-time' on both branches.
  initialDateTime?: { date: Date; startTime: string } | null;
}

export interface UseAvailabilityWizardReturn {
  // First-time flow
  isFirstTimeFlow: boolean;
  // Blocked state when no active services and can't create
  noActiveServicesBlocked: boolean;

  // Step navigation
  currentStep: WizardStep;
  totalSteps: number;
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: WizardStep) => void;
  isLastStep: boolean;

  // Service-type branch (new step 0 of normal flow)
  serviceType: AvailabilityServiceType | null;
  setServiceType: (kind: AvailabilityServiceType) => void;
  isGroupFlow: boolean; // true when serviceType === 'group'
  groupServiceId: number | null;
  setGroupServiceId: (id: number | null) => void;
  groupRecurrenceMode: GroupRecurrenceMode;
  setGroupRecurrenceMode: (m: GroupRecurrenceMode) => void;
  // Filtered service lists for the wizard's downstream steps.
  oneCustomerServices: ServiceOption[];
  groupServices: ServiceOption[];

  // Mode state
  mode: AvailabilityMode;
  setMode: (mode: AvailabilityMode) => void;

  // New service state (for first-time flow)
  newServiceName: string;
  setNewServiceName: (name: string) => void;
  newServiceDuration: number;
  setNewServiceDuration: (duration: number) => void;
  newServicePrice: string;
  setNewServicePrice: (price: string) => void;
  currency: string;

  // Service selection
  services: ServiceOption[];
  selectedServiceIds: number[];
  setSelectedServiceIds: (ids: number[]) => void;
  toggleService: (id: number) => void;
  selectAllServices: () => void;
  deselectAllServices: () => void;

  // Provider selection (for admin)
  providers: ProviderOption[];
  selectedProviderId: number | null;
  setSelectedProviderId: (id: number | null) => void;

  // Recurring state
  selectedDays: number[];
  setSelectedDays: (days: number[]) => void;
  recurrenceType: RecurrenceType;
  setRecurrenceType: (type: RecurrenceType) => void;
  recurrenceInterval: number;
  setRecurrenceInterval: (interval: number) => void;
  monthlyWeek: number;
  setMonthlyWeek: (week: number) => void;
  monthlyDay: number;
  setMonthlyDay: (day: number) => void;
  startType: StartType;
  setStartType: (type: StartType) => void;
  effectiveFrom: Date | null;
  setEffectiveFrom: (date: Date | null) => void;
  endType: EndType;
  setEndType: (type: EndType) => void;
  endDate: Date | null;
  setEndDate: (date: Date | null) => void;
  occurrences: number;
  setOccurrences: (count: number) => void;

  // Date range state
  dateRangeStart: Date | null;
  setDateRangeStart: (date: Date | null) => void;
  dateRangeEnd: Date | null;
  setDateRangeEnd: (date: Date | null) => void;
  dateRangeReason: string;
  setDateRangeReason: (reason: string) => void;

  // One-time state
  oneTimeDate: Date | null;
  setOneTimeDate: (date: Date | null) => void;
  oneTimeReason: string;
  setOneTimeReason: (reason: string) => void;

  // Time state (shared)
  startTime: string;
  setStartTime: (time: string) => void;
  endTime: string;
  setEndTime: (time: string) => void;

  // Summary
  summary: string;

  // Validation
  isStepValid: (step: WizardStep) => boolean;
  isFormValid: boolean;

  // Submit
  submit: () => Promise<void>;
  isSubmitting: boolean;

  // Reset
  reset: () => void;
}

export function useAvailabilityWizard({
  onCreateRecurring,
  onCreateDateRange,
  onCreateOneTime,
  onCreateGroupRecurring,
  onCreateGroupOneTime,
  onCreateService,
  services = [],
  providers = [],
  selectedProviderId: initialProviderId,
  onClose,
  currency = 'EUR',
  initialStep,
  initialDateTime,
}: UseAvailabilityWizardProps): UseAvailabilityWizardReturn {
  const { t } = useTranslation('staff');

  // Filter to get only active services
  const activeServices = useMemo(
    () => services.filter(s => s.active !== false),
    [services]
  );

  // Split active services by capacity so each branch of the wizard sees only
  // its own kind. A service with no max_capacity defaults to one-customer.
  const oneCustomerServices = useMemo(
    () => activeServices.filter(s => (s.max_capacity ?? 1) === 1),
    [activeServices]
  );
  const groupServices = useMemo(
    () => activeServices.filter(s => (s.max_capacity ?? 1) > 1),
    [activeServices]
  );

  // Determine if this is first-time flow (no active services exist)
  const isFirstTimeFlow = activeServices.length === 0 && !!onCreateService;

  // Flag for when there are no active services and can't create one
  const noActiveServicesBlocked = activeServices.length === 0 && !onCreateService;

  // Helper functions for translations
  const getDayFullName = useCallback(
    (dayIndex: number) => t(`availability.days.${DAY_FULL_KEYS[dayIndex]}`),
    [t]
  );
  const getWeekName = useCallback(
    (week: number) => t(`availability.recurrence.weekOptions.${WEEK_OPTION_KEYS[week]}`),
    [t]
  );

  // Step state.
  //   First-time flow: step 0 = WelcomeStep (existing behaviour).
  //   Normal flow:     step 0 = ServiceTypeStep (new picker), step 1+ = sub-flow.
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep ?? 0);

  // Service-type picker state. null until the user picks; group flow enables
  // the alternate step pipeline downstream.
  const [serviceType, setServiceTypeState] = useState<AvailabilityServiceType | null>(null);
  const isGroupFlow = serviceType === 'group';
  const [groupServiceId, setGroupServiceId] = useState<number | null>(null);
  const [groupRecurrenceMode, setGroupRecurrenceMode] = useState<GroupRecurrenceMode>('recurring');
  const setServiceType = useCallback((kind: AvailabilityServiceType) => {
    setServiceTypeState(kind);
  }, []);

  // Sync initialStep when it changes (e.g. reopening from conflict dialog)
  useEffect(() => {
    if (initialStep !== undefined) {
      setCurrentStep(initialStep);
    }
  }, [initialStep]);

  // Apply prefilled date/time from a calendar grid click. Forces both branches
  // into one-time mode so the prefill is meaningful.
  const prefillKey = initialDateTime
    ? `${initialDateTime.date.getTime()}|${initialDateTime.startTime}`
    : null;
  useEffect(() => {
    if (!initialDateTime) return;
    setOneTimeDate(initialDateTime.date);
    setStartTime(initialDateTime.startTime);
    setMode('one_time');
    setGroupRecurrenceMode('one_time');
  }, [prefillKey]);

  // Mode selection
  const [mode, setMode] = useState<AvailabilityMode>('recurring');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New service state (for first-time flow)
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState(60);
  const [newServicePrice, setNewServicePrice] = useState('');

  // Provider selection (for admin)
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(
    initialProviderId || null
  );

  // Service selection
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

  // Recurring availability state
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [monthlyWeek, setMonthlyWeek] = useState<number>(1);
  const [monthlyDay, setMonthlyDay] = useState<number>(1);
  const [startType, setStartType] = useState<StartType>('immediately');
  const [effectiveFrom, setEffectiveFrom] = useState<Date | null>(null);
  const [endType, setEndType] = useState<EndType>('never');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [occurrences, setOccurrences] = useState<number>(10);

  // Date range state
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
  const [dateRangeReason, setDateRangeReason] = useState('');

  // One-time state
  const [oneTimeDate, setOneTimeDate] = useState<Date | null>(null);
  const [oneTimeReason, setOneTimeReason] = useState('');

  // Time state (shared across modes)
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  // Sync initial provider when it changes
  useEffect(() => {
    if (initialProviderId !== undefined) {
      setSelectedProviderId(initialProviderId);
    }
  }, [initialProviderId]);

  // Nothing to choose: a single provider is selected for the user (the step
  // hides the select), and a lone service of a kind starts out selected (still
  // shown, can be unticked). Re-applied after every reset().
  const [resetCount, setResetCount] = useState(0);
  const soloProviderId = providers.length === 1 ? providers[0].id : null;
  const soloServiceId = oneCustomerServices.length === 1 ? oneCustomerServices[0].id : null;
  const soloGroupServiceId = groupServices.length === 1 ? groupServices[0].id : null;

  useEffect(() => {
    if (soloProviderId !== null && selectedProviderId === null) setSelectedProviderId(soloProviderId);
  }, [soloProviderId, selectedProviderId]);

  useEffect(() => {
    if (soloServiceId !== null) setSelectedServiceIds([soloServiceId]);
  }, [soloServiceId, resetCount]);

  useEffect(() => {
    if (soloGroupServiceId !== null) setGroupServiceId(soloGroupServiceId);
  }, [soloGroupServiceId, resetCount]);

  // Without group services the group option is blocked, so one-customer is
  // the only choice: start with it selected (the user just continues).
  const onlyOneCustomer = groupServices.length === 0 && oneCustomerServices.length > 0;
  useEffect(() => {
    if (onlyOneCustomer) setServiceTypeState((current) => current ?? 'one_customer');
  }, [onlyOneCustomer, resetCount]);

  // Calculate total steps based on mode and flow type
  const totalSteps = useMemo(() => {
    if (isFirstTimeFlow) {
      // First-time flow: Welcome(0) → CreateService(1) → TeamMember(2) → Schedule(3) → Review(4)
      return 5; // Steps 0-4
    }
    if (isGroupFlow) {
      // Group flow: TypePicker(0) → GroupService(1) → GroupRecurrence(2) → GroupReview(3)
      return 4; // Steps 0-3
    }
    // One-customer flow: TypePicker(0) → ModeAndServices(1) → Services(2) → Schedule+Time(3) → Review(4)
    return 5;
  }, [isFirstTimeFlow, isGroupFlow]);

  const isLastStep = isFirstTimeFlow
    ? currentStep === 4
    : isGroupFlow
      ? currentStep === 3
      : currentStep === 4;

  // Toggle service selection
  const toggleService = useCallback((id: number) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }, []);

  // Select / deselect all services
  const selectAllServices = useCallback(() => {
    setSelectedServiceIds(activeServices.map((s) => s.id));
  }, [activeServices]);

  const deselectAllServices = useCallback(() => {
    setSelectedServiceIds([]);
  }, []);

  // Reset form
  const reset = useCallback(() => {
    setResetCount((c) => c + 1);
    setCurrentStep(0);
    setServiceTypeState(null);
    setGroupServiceId(null);
    setGroupRecurrenceMode('recurring');
    setMode('recurring');
    setSelectedProviderId(initialProviderId || null);
    setSelectedServiceIds([]);
    setSelectedDays([1, 2, 3, 4, 5]); // Default to weekdays for better UX
    setRecurrenceType('weekly');
    setRecurrenceInterval(1);
    setMonthlyWeek(1);
    setMonthlyDay(1);
    setStartType('immediately');
    setEffectiveFrom(null);
    setEndType('never');
    setEndDate(null);
    setOccurrences(10);
    setDateRangeStart(null);
    setDateRangeEnd(null);
    setDateRangeReason('');
    setOneTimeDate(null);
    setOneTimeReason('');
    setStartTime('09:00');
    setEndTime('17:00');
    setIsSubmitting(false);
    // Reset new service state
    setNewServiceName('');
    setNewServiceDuration(60);
    setNewServicePrice('');
  }, [initialProviderId, isFirstTimeFlow]);

  // Generate summary text
  const summary = useMemo(() => {
    const timeRange = `${startTime} - ${endTime}`;

    if (mode === 'recurring') {
      if (recurrenceType === 'monthly') {
        const weekName = getWeekName(monthlyWeek);
        const dayName = getDayFullName(monthlyDay);
        let text = t('availability.summary.monthly', { week: weekName, day: dayName, timeRange });
        if (startType === 'on_date' && effectiveFrom) {
          text += t('availability.summary.startingFrom', { date: format(effectiveFrom, 'MMM d, yyyy') });
        }
        if (endType === 'on_date' && endDate) {
          text += t('availability.summary.until', { date: format(endDate, 'MMM d, yyyy') });
        } else if (endType === 'after_occurrences' && occurrences) {
          text += t('availability.summary.times', { count: occurrences });
        }
        return text;
      }

      if (selectedDays.length === 0) {
        return t('availability.summary.selectDays');
      }

      const dayNames = selectedDays.map((d) => getDayFullName(d));
      let text = '';

      if (recurrenceInterval === 1) {
        if (selectedDays.length === 7) {
          text = t('availability.summary.everyDay', { timeRange });
        } else if (
          selectedDays.length === 5 &&
          [1, 2, 3, 4, 5].every((d) => selectedDays.includes(d))
        ) {
          text = t('availability.summary.everyWeekday', { timeRange });
        } else {
          text = t('availability.summary.everyDays', { days: dayNames.join(', '), timeRange });
        }
      } else {
        text = t('availability.summary.everyNWeeks', {
          count: recurrenceInterval,
          days: dayNames.join(', '),
          timeRange,
        });
      }

      if (startType === 'on_date' && effectiveFrom) {
        text += t('availability.summary.startingFrom', { date: format(effectiveFrom, 'MMM d, yyyy') });
      }
      if (endType === 'on_date' && endDate) {
        text += t('availability.summary.until', { date: format(endDate, 'MMM d, yyyy') });
      } else if (endType === 'after_occurrences' && occurrences) {
        text += t('availability.summary.times', { count: occurrences });
      }

      return text;
    }

    if (mode === 'date_range') {
      if (!dateRangeStart || !dateRangeEnd) {
        return t('availability.summary.selectDateRange');
      }
      const days = differenceInDays(dateRangeEnd, dateRangeStart) + 1;
      return t('availability.summary.available', {
        startDate: format(dateRangeStart, 'MMM d'),
        endDate: format(dateRangeEnd, 'MMM d, yyyy'),
        timeRange,
        days,
      });
    }

    if (mode === 'one_time') {
      if (!oneTimeDate) {
        return t('availability.summary.selectDate');
      }
      return t('availability.summary.availableOn', {
        date: format(oneTimeDate, 'EEEE, MMM d, yyyy'),
        timeRange,
      });
    }

    return '';
  }, [
    mode,
    selectedDays,
    recurrenceType,
    recurrenceInterval,
    monthlyWeek,
    monthlyDay,
    startType,
    effectiveFrom,
    endType,
    endDate,
    occurrences,
    dateRangeStart,
    dateRangeEnd,
    oneTimeDate,
    startTime,
    endTime,
    t,
    getDayFullName,
    getWeekName,
  ]);

  // Step validation
  const isStepValid = useCallback(
    (step: WizardStep): boolean => {
      // First-time flow validation
      if (isFirstTimeFlow) {
        switch (step) {
          case 0: // Welcome
            return true;
          case 1: // Create Service
            return newServiceName.trim().length > 0 && newServiceDuration > 0;
          case 2: // Team Member
            return providers.length === 0 || selectedProviderId !== null;
          case 3: // Schedule (time selection)
            return startTime < endTime && selectedDays.length > 0;
          case 4: // Review
            return true;
          default:
            return false;
        }
      }

      // Normal-flow step 0: pick the service-type (group vs one-customer).
      if (step === 0) {
        return serviceType !== null;
      }

      // Group flow has its own (shorter) pipeline.
      if (isGroupFlow) {
        switch (step) {
          case 1: // GroupServiceStep
            if (groupServiceId === null) return false;
            if (providers.length > 0 && !selectedProviderId) return false;
            return true;
          case 2: // GroupRecurrenceStep / GroupOneTimeStep
            if (groupRecurrenceMode === 'one_time') {
              return oneTimeDate !== null;
            }
            // Recurring: monthly needs week+day, otherwise needs at least one day-of-week,
            // and a sane end condition.
            if (recurrenceType === 'monthly') {
              if (monthlyWeek === null || monthlyDay === null) return false;
            } else if (selectedDays.length === 0) {
              return false;
            }
            if (endType === 'on_date' && !endDate) return false;
            if (endType === 'after_occurrences' && (!occurrences || occurrences <= 0)) return false;
            return true;
          case 3: // GroupReviewStep
            return true;
          default:
            return false;
        }
      }

      // One-customer normal flow validation (existing behaviour)
      // Step 1: Type + Provider | Step 2: Services | Step 3: Schedule | Step 4: Time | Step 5: Review
      // One-time: Step 1: Type + Provider | Step 2: Services | Step 3: DateTime | Step 4: Review
      switch (step) {
        case 1:
          // Mode must be selected, provider required if admin
          if (providers.length > 0 && !selectedProviderId) return false;
          return true;

        case 2:
          // Services step - at least one service must be selected
          if (services.length > 0 && selectedServiceIds.length === 0) return false;
          return true;

        case 3:
          // Step 3 now includes time inputs for all modes
          if (startTime >= endTime) return false;
          if (mode === 'recurring') {
            if (recurrenceType === 'monthly') {
              return monthlyWeek !== null && monthlyDay !== null;
            }
            return selectedDays.length > 0;
          }
          if (mode === 'date_range') {
            return dateRangeStart !== null && dateRangeEnd !== null;
          }
          if (mode === 'one_time') {
            return oneTimeDate !== null;
          }
          return false;

        case 4:
          // Review step
          return true;

        default:
          return false;
      }
    },
    [
      isFirstTimeFlow,
      newServiceName,
      newServiceDuration,
      mode,
      services.length,
      selectedServiceIds.length,
      providers.length,
      selectedProviderId,
      recurrenceType,
      monthlyWeek,
      monthlyDay,
      selectedDays.length,
      dateRangeStart,
      dateRangeEnd,
      oneTimeDate,
      startTime,
      endTime,
      serviceType,
      isGroupFlow,
      groupServiceId,
      groupRecurrenceMode,
      endType,
      endDate,
      occurrences,
    ]
  );

  // Overall form validation
  const isFormValid = useMemo(() => {
    // First-time flow validation (group + window both share the same shell here)
    if (isFirstTimeFlow) {
      if (startTime >= endTime) return false;
      if (newServiceName.trim().length === 0 || newServiceDuration <= 0) return false;
      if (providers.length > 0 && !selectedProviderId) return false;
      return selectedDays.length > 0;
    }

    // Service-type picker must have been answered.
    if (serviceType === null) return false;

    // Group flow: end time is computed from service.duration_minutes, so the
    // start_time < end_time check is moot here. Validate the group-specific
    // shape instead.
    if (isGroupFlow) {
      if (groupServiceId === null) return false;
      if (providers.length > 0 && !selectedProviderId) return false;
      if (groupRecurrenceMode === 'one_time') {
        return oneTimeDate !== null;
      }
      // Recurring: cadence + end-condition.
      if (recurrenceType === 'monthly') {
        if (monthlyWeek === null || monthlyDay === null) return false;
      } else if (selectedDays.length === 0) {
        return false;
      }
      if (endType === 'on_date' && !endDate) return false;
      if (endType === 'after_occurrences' && (!occurrences || occurrences <= 0)) return false;
      return true;
    }

    // One-customer flow validation (existing behaviour)
    if (startTime >= endTime) return false;
    if (services.length > 0 && selectedServiceIds.length === 0) return false;
    if (providers.length > 0 && !selectedProviderId) return false;

    if (mode === 'recurring') {
      if (recurrenceType === 'monthly') {
        return monthlyWeek !== null && monthlyDay !== null;
      }
      return selectedDays.length > 0;
    }

    if (mode === 'date_range') {
      return dateRangeStart !== null && dateRangeEnd !== null;
    }

    if (mode === 'one_time') {
      return oneTimeDate !== null;
    }

    return false;
  }, [
    isFirstTimeFlow,
    newServiceName,
    newServiceDuration,
    mode,
    selectedDays.length,
    recurrenceType,
    monthlyWeek,
    monthlyDay,
    dateRangeStart,
    dateRangeEnd,
    oneTimeDate,
    startTime,
    endTime,
    services.length,
    selectedServiceIds.length,
    providers.length,
    selectedProviderId,
    serviceType,
    isGroupFlow,
    groupServiceId,
    groupRecurrenceMode,
    endType,
    endDate,
    occurrences,
  ]);

  // Navigation
  const canGoNext = isStepValid(currentStep);
  // Step 0 is always the entry point — no back from there.
  const canGoBack = currentStep > 0;

  const goNext = useCallback(() => {
    if (canGoNext && currentStep < totalSteps) {
      setCurrentStep((prev) => (prev + 1) as WizardStep);
    }
  }, [canGoNext, currentStep, totalSteps]);

  const goBack = useCallback(() => {
    if (canGoBack) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  }, [canGoBack]);

  const goToStep = useCallback((step: WizardStep) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
    }
  }, []);

  // Submit handler
  const submit = useCallback(async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // First-time flow: Create service first, then availability
      if (isFirstTimeFlow && onCreateService) {
        // Create the service
        const newService = await onCreateService({
          name: newServiceName.trim(),
          duration: newServiceDuration,
          price: newServicePrice,
        });

        // Create recurring availability with the new service
        await onCreateRecurring({
          days_of_week: selectedDays,
          start_time: startTime,
          end_time: endTime,
          recurrence_type: 'weekly',
          recurrence_interval: 1,
          monthly_week: null,
          monthly_day: null,
          end_type: 'never',
          end_date: null,
          occurrences: null,
          service_ids: [newService.id],
          provider_id: selectedProviderId || undefined,
        });
      } else if (isGroupFlow) {
        if (groupServiceId === null) throw new Error('group service required');
        if (groupRecurrenceMode === 'recurring') {
          if (!onCreateGroupRecurring) throw new Error('onCreateGroupRecurring not wired');
          await onCreateGroupRecurring({
            service_id: groupServiceId,
            days_of_week: recurrenceType === 'monthly' ? [monthlyDay!] : selectedDays,
            start_time: startTime,
            recurrence_type: recurrenceType,
            recurrence_interval: recurrenceInterval,
            monthly_week: recurrenceType === 'monthly' ? monthlyWeek : null,
            monthly_day: recurrenceType === 'monthly' ? monthlyDay : null,
            end_type: endType,
            end_date: endType === 'on_date' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
            occurrences: endType === 'after_occurrences' ? occurrences : null,
            effective_from: startType === 'on_date' && effectiveFrom ? format(effectiveFrom, 'yyyy-MM-dd') : undefined,
            provider_id: selectedProviderId || undefined,
          });
        } else {
          if (!onCreateGroupOneTime) throw new Error('onCreateGroupOneTime not wired');
          if (!oneTimeDate) throw new Error('group one-time date required');
          await onCreateGroupOneTime({
            service_id: groupServiceId,
            date: format(oneTimeDate, 'yyyy-MM-dd'),
            start_time: startTime,
            provider_id: selectedProviderId || undefined,
          });
        }
      } else if (mode === 'recurring') {
        await onCreateRecurring({
          days_of_week: recurrenceType === 'monthly' ? [monthlyDay!] : selectedDays,
          start_time: startTime,
          end_time: endTime,
          recurrence_type: recurrenceType,
          recurrence_interval: recurrenceInterval,
          monthly_week: recurrenceType === 'monthly' ? monthlyWeek : null,
          monthly_day: recurrenceType === 'monthly' ? monthlyDay : null,
          end_type: endType,
          end_date: endType === 'on_date' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
          occurrences: endType === 'after_occurrences' ? occurrences : null,
          effective_from: startType === 'on_date' && effectiveFrom ? format(effectiveFrom, 'yyyy-MM-dd') : undefined,
          service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
          provider_id: selectedProviderId || undefined,
        });
      } else if (mode === 'date_range') {
        await onCreateDateRange({
          start_date: format(dateRangeStart!, 'yyyy-MM-dd'),
          end_date: format(dateRangeEnd!, 'yyyy-MM-dd'),
          start_time: startTime,
          end_time: endTime,
          reason: dateRangeReason || undefined,
          provider_id: selectedProviderId || undefined,
          service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
        });
      } else if (mode === 'one_time') {
        await onCreateOneTime({
          date: format(oneTimeDate!, 'yyyy-MM-dd'),
          start_time: startTime,
          end_time: endTime,
          reason: oneTimeReason || undefined,
          provider_id: selectedProviderId || undefined,
          service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
        });
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create availability:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isFormValid,
    isSubmitting,
    isFirstTimeFlow,
    onCreateService,
    newServiceName,
    newServiceDuration,
    newServicePrice,
    mode,
    onCreateRecurring,
    onCreateDateRange,
    onCreateOneTime,
    recurrenceType,
    monthlyDay,
    selectedDays,
    startTime,
    endTime,
    recurrenceInterval,
    monthlyWeek,
    startType,
    effectiveFrom,
    endType,
    endDate,
    occurrences,
    selectedServiceIds,
    selectedProviderId,
    dateRangeStart,
    dateRangeEnd,
    dateRangeReason,
    oneTimeDate,
    oneTimeReason,
    reset,
    onClose,
    isGroupFlow,
    groupServiceId,
    groupRecurrenceMode,
    onCreateGroupRecurring,
    onCreateGroupOneTime,
  ]);

  return {
    // First-time flow
    isFirstTimeFlow,
    // Blocked state when no active services and can't create
    noActiveServicesBlocked,

    // Step navigation
    currentStep,
    totalSteps,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    goToStep,
    isLastStep,

    // Service-type branch
    serviceType,
    setServiceType,
    isGroupFlow,
    groupServiceId,
    setGroupServiceId,
    groupRecurrenceMode,
    setGroupRecurrenceMode,
    oneCustomerServices,
    groupServices,

    // Mode state
    mode,
    setMode,

    // New service state (for first-time flow)
    newServiceName,
    setNewServiceName,
    newServiceDuration,
    setNewServiceDuration,
    newServicePrice,
    setNewServicePrice,
    currency,

    // Service selection (only active services)
    services: activeServices,
    selectedServiceIds,
    setSelectedServiceIds,
    toggleService,
    selectAllServices,
    deselectAllServices,

    // Provider selection
    providers,
    selectedProviderId,
    setSelectedProviderId,

    // Recurring state
    selectedDays,
    setSelectedDays,
    recurrenceType,
    setRecurrenceType,
    recurrenceInterval,
    setRecurrenceInterval,
    monthlyWeek,
    setMonthlyWeek,
    monthlyDay,
    setMonthlyDay,
    startType,
    setStartType,
    effectiveFrom,
    setEffectiveFrom,
    endType,
    setEndType,
    endDate,
    setEndDate,
    occurrences,
    setOccurrences,

    // Date range state
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    dateRangeReason,
    setDateRangeReason,

    // One-time state
    oneTimeDate,
    setOneTimeDate,
    oneTimeReason,
    setOneTimeReason,

    // Time state
    startTime,
    setStartTime,
    endTime,
    setEndTime,

    // Summary
    summary,

    // Validation
    isStepValid,
    isFormValid,

    // Submit
    submit,
    isSubmitting,

    // Reset
    reset,
  };
}
